<script setup lang="ts">
import TeacherIllustration from '~/assets/images/illustrations/teachers.svg';
import type { FetchError } from 'ofetch';

const { user } = useUserStore();
const { login } = useAuthentication();
const route = useRoute();
const socialLoginStatus = route.query.status as string;
const socialLoginHint = route.query.hint as string | null;
const submitting = ref(false);
const { dispatchTrackingEvent, hashEmailAndPush, hashEmailAndPushOnBlur, virtualPageView } = useTracking();

virtualPageView('authentication', 'login');

const signUpLink = computed(() => {
  const link: any = {
    path: '/auth/signup',
  };

  if (route.query.next) {
    link.query = { next: route.query.next };
  }

  return link;
});

const formData = ref({
  email: '',
  password: '',
});

const error = reactive({
  email: '',
  password: '',
});

const submitForm = async () => {
  submitting.value = true;
  try {
    await hashEmailAndPush(formData.value.email);
    await login(formData.value.email, formData.value.password);

    dispatchTrackingEvent('login', { method: 'email' });

    if (typeof route.query.next === 'string') {
      navigateTo(route.query.next);
    } else {
      navigateTo('/dashboard');
    }
  } catch (err) {
    submitting.value = false;
    if ((err as FetchError)?.statusCode === 422) {
      const _err = err as FetchError;
      if (_err.data?.errors?.email[0]) {
        error.email = _err.data.errors.email[0];
      }
    }
  }
};

onMounted(async () => {
  if (user.value) {
    navigateTo(route.query.next as string || '/dashboard');
  }

  if (route.query.email) {
    formData.value.email = route.query.email as string;
  }
});

definePageMeta({
  layout: 'auth',
});

useSeoMeta({
  title: 'Sign in',
});

useHead({
  title: 'Sign in',
});
</script>

<template>
  <div class="page page--login">
    <SplitView class="content">
      <template #main>
        <form
          class="flex flex-col gap-4"
          @submit.prevent="submitForm"
        >
          <header class="form-heading">
            <AuthPagesFormHeader
              title="Log in to Medmastery"
              subtitle="It’s good to have you back!"
            />
          </header>

          <div class="flex flex-col gap-4">
            <MField
              label="Email *"
              :error="error.email"
            >
              <InputText
                v-model="formData.email"
                :invalid="!!error.email"
                :disabled="submitting"
                type="email"
                autocomplete="username"
                @blur="hashEmailAndPushOnBlur"
              />
            </MField>
            <MField label="Password *">
              <Password
                v-model="formData.password"
                toggle-mask
                :invalid="!!error.password"
                :disabled="submitting"
                :feedback="false"
                :input-props="{autocomplete: 'current-password'}"
              />
            </MField>
            <Button
              label="Log in"
              type="submit"
              :disabled="submitting"
            >
              <template #icon>
                <MIcon
                  icon="arrow-right"
                  class="p-button-icon p-button-icon-right"
                />
              </template>
            </Button>
          </div>

          <div class="flex flex-col gap-4 text-14">
            <NuxtLink to="/auth/forgot-password">
              Forgot your password?
            </NuxtLink>
            <span>
              Don't have an account yet? <NuxtLink :to="signUpLink">Sign up</NuxtLink>
            </span>
          </div>
          <Divider
            :align="'center'"
            class="my-4 text-14 font-bold leading-normal"
          >
            OR
          </Divider>
          <AuthPagesSocialLogin
            :button-prefix="'Log in'"
            :social-login-hint="socialLoginHint"
            :social-login-status="socialLoginStatus"
            :track-as-login-event="true"
          />
        </form>
      </template>
      <template #aside>
        <div class="why-sign-up">
          <TeacherIllustration class="w-full h-auto" />
        </div>
      </template>
    </SplitView>
  </div>
</template>

<style lang="scss">
@import '@mui/assets/styles/utility.scss';

.page--login {
  @include breakpoint ('medium') {
    background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 50%, var(--palette-neutral-10) 50%, var(--palette-neutral-10) 100%);
  }

  .content {
    @include container-sizer('page');
    height: 100%;
  }

  .p-divider-content {
    background-color: var(--palette-neutral-5);

    @include breakpoint ('medium') {
      background-color: white;
    }
  }
}
</style>